import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Navbar from "../../components/Navbar";

import image1 from "../../data/obory/hudebni/3c299d12-ba9b-4bce-94ee-0c4e838a63c4.jpeg"
import image2 from "../../data/obory/hudebni/543b0903-761d-4008-8426-352fa8a17751a.jpeg"
import image3 from "../../data/obory/hudebni/DSC_6942.jpg"
import image4 from "../../data/obory/hudebni/DSC_6952.jpg"
import image5 from "../../data/obory/hudebni/DSC_7091.jpg"
import image6 from "../../data/obory/hudebni/IMG_4969.jpeg"

// Color variables
const primaryColor = "#4f7971";
const primaryColorLight = "#6a8d86";
const primaryColorLighter = "#85a29c";
const textOnPrimary = "#ffffff";

interface SliderImage {
  src: string;
  alt: string;
  link: string;
}

const sliderImages: SliderImage[] = [
  {
    src: image5,
    alt: "School concert",
    link: "/gallery"
  },
  {
    src: image2,
    alt: "Music lessons",
    link: "/gallery"
  },
  {
    src: image3,
    alt: "Student performance",
    link: "/gallery"
  },
  {
    src: image6,
    alt: "Student performance",
    link: "/gallery"
  },
  {
    src: image4,
    alt: "Student performance",
    link: "/gallery"
  },
  {
    src: image1,
    alt: "Student performance",
    link: "/gallery"
  }
];

interface Instrument {
  name: string;
  icon: string;
}

const Hudebni: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === sliderImages.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? sliderImages.length - 1 : prev - 1));
  };

  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const instruments: Instrument[] = [
    { name: "Baskřídlovka", icon: "🎺" },
    { name: "Basová kytara", icon: "🎸" },
    { name: "Bicí", icon: "🥁" },
    { name: "Elektronická kytara", icon: "🎸" },
    { name: "Elektronické klávesy", icon: "🎹" },
    { name: "Housle", icon: "🎻" },
    { name: "Klarinet", icon: "🎷" },
    { name: "Klavír", icon: "🎹" },
    { name: "Kytara", icon: "🎸" },
    { name: "Příčná flétna", icon: "🎶" },
    { name: "Zobcová flétna", icon: "🎶" },
    { name: "Saxofon", icon: "🎷" },
    { name: "Trumpeta", icon: "🎺" },
    { name: "Violoncello", icon: "🎻" },
    { name: "Kontrabas", icon: "🎻" },
    { name: "Zpěv", icon: "🧑‍🎤"}
  ];

  return (
    <div className="min-h-screen bg-[rgba(79,121,113,0.1)] pt-[80px]">
      {/* Navbar */}
      <Navbar />
      
      {/* Full-width Hero Slider */}
      <div className="relative w-screen -ml-[calc(50vw-50%)] h-[90vh]">
        {sliderImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`}
          >
            <img
              src={image.src}
              alt={image.alt}
              className="w-full h-full object-cover"
            />
            <Link to={image.link}>
              <div 
                className="absolute inset-0 flex items-center justify-center"
                style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
              >
                <h2 className="text-5xl md:text-7xl font-bold text-white text-center">
                  Hudební obor
                </h2>
              </div>
            </Link>
          </div>
        ))}
        
        {/* Navigation arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/30 hover:bg-white/50 text-white p-2 rounded-full transition-all"
          aria-label="Previous slide"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/30 hover:bg-white/50 text-white p-2 rounded-full transition-all"
          aria-label="Next slide"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
        
        {/* Dots indicator */}
        <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-2">
          {sliderImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${index === currentSlide ? 'bg-white w-6' : 'bg-white/50'}`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Main content container */}
      <div className="container mx-auto px-4 sm:px-6 pt-16 pb-12">
        {/* About section */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
          <div className="p-6 md:p-10">
            <div className="flex items-center mb-6">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800">O hudebním oboru</h2>
            </div>
            <div className="space-y-4 md:space-y-6 text-base md:text-lg text-gray-700">
              <p className="leading-relaxed">
                Cílem hudebního oboru je rozvíjet schopnost žáků využívat hudbu jako nástroj pro vzájemnou komunikaci a osobní umělecký výraz. Výuka klade důraz na individuální přístup, aktivní zapojení do interpretace, poznávání hudební kultury a získání základních teoretických znalostí.
              </p>
              <p className="leading-relaxed">
                Obor nabízí širokou škálu výuky hudebních nástrojů, zahrnující klávesové, strunné, dechové a bicí nástroje, stejně jako sólový zpěv.
              </p>
              <p className="leading-relaxed">
                Kromě individuální výuky je součástí i předmět hudební nauka, který probíhá ve skupinách a zaměřuje se na výuku hudební teorie, dějin hudby, nauky o nástrojích a další související témata.
              </p>
              <p className="leading-relaxed">
                Ve vyšších ročnících se žáci pravidelně zapojují do skupinové výuky, která je organizována prostřednictvím různých souborů, orchestrů, pěveckých sborů a dalších komorních uskupení.
              </p>
            </div>
          </div>
        </div>

        {/* Instruments section */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
          <div className="p-6 md:p-10">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">Nabízené nástroje</h2>
            <p className="text-base md:text-lg text-gray-600 mb-6 md:mb-8">
              Náš hudební obor nabízí rozmanitou škálu hudebních nástrojů, se kterými tě naučíme zacházet.
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {instruments.map((nastroj) => (
                <div 
                  key={nastroj.name} 
                  className="flex items-start p-3 md:p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-all border border-gray-200 hover:border-gray-300 group"
                >
                  <span className="text-2xl md:text-3xl mr-3 md:mr-4 group-hover:text-teal-800 transition-colors">
                    {nastroj.icon}
                  </span>
                  <div>
                    <h3 className="text-lg md:text-xl font-semibold text-gray-800 group-hover:text-teal-900 transition-colors">
                      {nastroj.name}
                    </h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div 
          className="rounded-xl shadow-lg overflow-hidden"
          style={{ backgroundColor: primaryColor }}
        >
          <div className="p-6 md:p-12 text-center">
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
              Máte zájem o studium hudby?
            </h2>
            <div className="flex flex-col sm:flex-row justify-center gap-3 md:gap-4">
              <Link
                to="/chcinazus/prijmacirizeni"
                className="px-6 md:px-8 py-2 md:py-3 bg-white font-medium rounded-lg transition-all transform hover:scale-105"
                style={{ color: primaryColor }}
              >
                Přihláška ke studiu
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hudebni;